/**
 * «ثبّت رفرفة على جوالك» (25 سبتمبر 2026).
 *
 * كروم على أندرويد يطلق `beforeinstallprompt` **مرّةً واحدة وفي أول الصفحة** — قبل أن تُركَّب
 * القشرة وقبل الدخول غالباً. فيُلتقط هنا عند تحميل الوحدة (تُستورد من `main.tsx`) ويُحفظ،
 * ويقرؤه البانر حين يظهر. وسفاري آيفون لا يطلقه أصلاً: التثبيتُ هناك من «مشاركة ← إضافة
 * إلى الشاشة الرئيسية» يدوياً، فيقول البانرُ ذلك بدل زرٍّ لا يعمل.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // لا بانرَ من المتصفّح نفسِه — بانرُنا بكلامنا ومتى نشاء
    deferred = e as BeforeInstallPromptEvent;
    listeners.forEach((l) => l());
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    listeners.forEach((l) => l());
  });
}

export function canPromptInstall(): boolean { return deferred !== null; }

export function onInstallAvailability(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** يفتح نافذة التثبيت الأصلية؛ ويعيد true إن قبل المستخدم */
export async function promptInstall(): Promise<boolean> {
  if (!deferred) return false;
  const ev = deferred;
  deferred = null;
  await ev.prompt();
  const choice = await ev.userChoice;
  listeners.forEach((l) => l());
  return choice.outcome === 'accepted';
}

/** آيفون/آيباد في سفاري — حيث التثبيتُ يدويٌّ من قائمة المشاركة */
export function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const ios = /iPhone|iPad|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
  return ios && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

const DISMISS_KEY = 'rafrafa.install.dismissedAt';
const REMIND_AFTER_MS = 30 * 86_400_000;

/** أُغلق البانر خلال الثلاثين يوماً الماضية؟ (التخزينُ قد يُحجب — فالافتراضُ «لم يُغلق») */
export function installDismissedRecently(): boolean {
  try {
    const v = Number(localStorage.getItem(DISMISS_KEY) ?? 0);
    return v > 0 && Date.now() - v < REMIND_AFTER_MS;
  } catch { return false; }
}

export function dismissInstall(): void {
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* التخزينُ محجوب — يعود البانرُ في الزيارة التالية */ }
}
